/**
 * @CODE:ACC-003-HOOK-IMAGES-001
 * useAccommodationImages Hook
 *
 * Fetches all images for a specific accommodation
 * - Queries accommodation_images table
 * - Orders by image_order (or id as fallback)
 * - Returns all images (not just main_image)
 * - Used for image carousel in AccommodationDetailPage
 */

import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "@/lib/supabase";
import type { AccommodationImage } from "../types";

export const useAccommodationImages = (accommodationId: number | string) => {
  return useQuery({
    queryKey: ["accommodation-images", accommodationId],
    queryFn: async (): Promise<AccommodationImage[]> => {
      try {
        const client = getSupabaseClient();
        const { data, error } = await client
          .from("accommodation_images")
          .select("*")
          .eq("accommodation_id", accommodationId)
          .order("is_main_photo", { ascending: false }) // Main photo first
          .order("id", { ascending: true }); // Then by id

        if (error) {
          console.error("Error fetching accommodation images:", error);
          throw new Error("Failed to fetch accommodation images");
        }

        return data || [];
      } catch (error) {
        console.error('❌ 숙소 이미지 조회 중 오류 발생:', error);

        // 타임아웃이나 네트워크 오류 시 빈 배열 반환
        if (error instanceof Error && error.message.includes('timeout')) {
          console.warn('⏱️ 쿼리 타임아웃 - 빈 배열 반환');
          return [];
        }
        throw error;
      }
    },
    enabled: !!accommodationId,
    staleTime: 1000 * 60 * 10, // 10분간 fresh 상태 유지
    gcTime: 1000 * 60 * 30, // 30분간 캐시 유지
    retry: (failureCount, error) => {
      // 연결 오류인 경우 재시도
      if (error && error.message && (error.message.includes('network') || error.message.includes('fetch'))) {
        return failureCount < 3;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
  });
};
